import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FileText, Plus, MoreVertical, Link as LinkIcon, Trash2, 
  Eye, Edit, LogOut, CheckCircle, XCircle, ExternalLink, Copy, Users, LayoutTemplate, BookTemplate, Star
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';

const DashboardPage = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteFormId, setDeleteFormId] = useState(null);
  const [zohoStatus, setZohoStatus] = useState(false);
  const [connectingZoho, setConnectingZoho] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(false);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const fetchForms = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/forms`, {
        withCredentials: true
      });
      setForms(response.data);
    } catch (error) {
      console.error('Error fetching forms:', error);
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchZohoStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/zoho/status`, {
        withCredentials: true
      });
      setZohoStatus(response.data.connected);
    } catch (error) {
      console.error('Error fetching Zoho status:', error);
    }
  }, []);

  useEffect(() => {
    fetchForms();
    fetchZohoStatus();
    
    // Check for Zoho OAuth callback
    const zohoConnected = searchParams.get('zoho_connected');
    const zohoError = searchParams.get('zoho_error');
    const googleParam = searchParams.get('google');
    
    if (zohoConnected === 'true') {
      toast.success('Successfully connected to Zoho Sheets!');
      refreshUser();
      fetchZohoStatus();
      window.history.replaceState({}, '', '/dashboard');
    } else if (zohoError) {
      toast.error(`Zoho connection failed: ${zohoError}`);
      window.history.replaceState({}, '', '/dashboard');
    }
    
    if (googleParam === 'connected') {
      toast.success('Successfully connected to Google Sheets!');
      refreshUser();
      setGoogleStatus(true);
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [fetchForms, fetchZohoStatus, searchParams, refreshUser]);

  const handleCreateForm = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/forms`, {
        title: 'Untitled Form',
        description: '',
        fields: []
      }, { withCredentials: true });
      navigate(`/forms/${response.data.id}/edit`);
    } catch (error) {
      toast.error('Failed to create form');
    }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await axios.get(`${API_URL}/api/templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoadingTemplates(false); }
  };

  const handleUseTemplate = async (templateId) => {
    try {
      const response = await axios.post(`${API_URL}/api/templates/${templateId}/use`, {}, { withCredentials: true });
      toast.success('Form created from template!');
      setShowTemplates(false);
      navigate(`/forms/${response.data.id}/edit`);
    } catch { toast.error('Failed to create form from template'); }
  };

  const handleSaveAsTemplate = async (formId) => {
    try {
      await axios.post(`${API_URL}/api/templates`, { form_id: formId }, { withCredentials: true });
      toast.success('Saved as template!');
    } catch { toast.error('Failed to save as template'); }
  };

  const handleDuplicateForm = async (formId) => {
    try {
      const response = await axios.post(`${API_URL}/api/forms/${formId}/duplicate`, {}, { withCredentials: true });
      toast.success('Form duplicated!');
      navigate(`/forms/${response.data.id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate form');
    }
  };

  const handleDeleteForm = async () => {
    if (!deleteFormId) return;
    try {
      await axios.delete(`${API_URL}/api/forms/${deleteFormId}`, {
        withCredentials: true
      });
      setForms(forms.filter(f => f.id !== deleteFormId));
      toast.success('Form deleted');
    } catch (error) {
      toast.error('Failed to delete form');
    } finally {
      setDeleteFormId(null);
    }
  };

  const handleCopyLink = (formId) => {
    const link = `${window.location.origin}/forms/${formId}`;
    setShareLink(link);
    setShowShareDialog(true);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success('Link copied!');
    } catch (err) {
      const input = document.getElementById('share-link-input');
      if (input) {
        input.select();
        document.execCommand('copy');
        toast.success('Link copied!');
      }
    }
  };

  const handleConnectZoho = async () => {
    setConnectingZoho(true);
    try {
      const response = await axios.get(`${API_URL}/api/zoho/authorize`, {
        withCredentials: true
      });
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      } else {
        toast.error('Invalid authorization URL received');
        setConnectingZoho(false);
      }
    } catch (error) {
      console.error('Zoho connection error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to start Zoho connection: ' + (error.response?.data?.detail || error.message));
      }
      setConnectingZoho(false);
    }
  };

  const handleDisconnectZoho = async () => {
    try {
      await axios.post(`${API_URL}/api/zoho/disconnect`, {}, { withCredentials: true });
      setZohoStatus(false);
      refreshUser();
      toast.success('Disconnected from Zoho');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleConnectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      window.location.href = `${API_URL}/api/google/login`;
    } catch (error) {
      toast.error('Failed to start Google connection');
      setConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await axios.post(`${API_URL}/api/google/disconnect`, {}, { withCredentials: true });
      setGoogleStatus(false);
      refreshUser();
      toast.success('Disconnected from Google');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/refex-logo.png" alt="Refex" className="h-9 w-auto" />
              <span className="text-xl font-semibold font-['Outfit'] text-slate-900">Forms</span>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600 hidden sm:block">{user?.email}</span>
              {user?.role === 'admin' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                  data-testid="admin-panel-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Admin Panel
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-600 hover:text-slate-900"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Zoho Connection Card */}
        <Card className="mb-8 border-slate-200" data-testid="zoho-connection-card">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <img 
                    src="https://www.zohowebstatic.com/sites/default/files/styles/product-home-page/public/sheet-logo.png"
                    alt="Zoho Sheets"
                    className="w-6 h-6"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div>
                  <CardTitle className="text-lg font-['Outfit']">Zoho Sheets Integration</CardTitle>
                  <CardDescription>Connect to sync form responses automatically</CardDescription>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {zohoStatus ? (
                  <>
                    <span className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDisconnectZoho}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="disconnect-zoho-btn"
                    >
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-2 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not connected
                    </span>
                    <Button
                      onClick={handleConnectZoho}
                      disabled={connectingZoho}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="connect-zoho-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {connectingZoho ? 'Connecting...' : 'Connect Zoho'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Google Sheets Connection Card */}
        <Card className="mb-8 border-slate-200" data-testid="google-connection-card">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Google_Sheets_logo_%282014-2020%29.svg/1200px-Google_Sheets_logo_%282014-2020%29.svg.png"
                    alt="Google Sheets"
                    className="w-6 h-6"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div>
                  <CardTitle className="text-lg font-['Outfit']">Google Sheets Integration</CardTitle>
                  <CardDescription>Connect to sync form responses to Google Sheets</CardDescription>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {(googleStatus || user?.google_connected) ? (
                  <>
                    <span className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDisconnectGoogle}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="disconnect-google-btn"
                    >
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-2 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not connected
                    </span>
                    <Button
                      onClick={handleConnectGoogle}
                      disabled={connectingGoogle}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="connect-google-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {connectingGoogle ? 'Connecting...' : 'Connect Google'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Forms Section */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold font-['Outfit'] text-slate-900">My Forms</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowTemplates(true); fetchTemplates(); }}
              data-testid="templates-btn"
            >
              <LayoutTemplate className="w-4 h-4 mr-2" />
              Templates
            </Button>
            <Button
              onClick={handleCreateForm}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="create-form-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Form
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-slate-100 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : forms.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-300 bg-white/50">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2 font-['Outfit']">No forms yet</h3>
              <p className="text-slate-500 mb-6 text-center max-w-sm">
                Create your first form to start collecting responses
              </p>
              <Button
                onClick={handleCreateForm}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="create-first-form-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create your first form
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {forms.map((form) => (
              <Card 
                key={form.id} 
                className="hover:shadow-md transition-shadow cursor-pointer group"
                data-testid={`form-card-${form.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => navigate(`/forms/${form.id}/edit`)}
                    >
                      <h3 className="font-semibold text-slate-900 font-['Outfit'] truncate group-hover:text-indigo-600 transition-colors">
                        {form.title || 'Untitled Form'}
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        {form.response_count || 0} responses
                      </p>
                    </div>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`form-menu-${form.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/forms/${form.id}/edit`)} data-testid={`edit-form-${form.id}`}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/forms/${form.id}`)} data-testid={`preview-form-${form.id}`}>
                          <Eye className="w-4 h-4 mr-2" />
                          Preview
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/forms/${form.id}/responses`)} data-testid={`view-responses-${form.id}`}>
                          <FileText className="w-4 h-4 mr-2" />
                          View Responses
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCopyLink(form.id)} data-testid={`copy-link-${form.id}`}>
                          <LinkIcon className="w-4 h-4 mr-2" />
                          Copy Link
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicateForm(form.id)} data-testid={`duplicate-form-${form.id}`}>
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        {user?.role === 'admin' && (
                          <DropdownMenuItem onClick={() => handleSaveAsTemplate(form.id)} data-testid={`save-template-${form.id}`}>
                            <BookTemplate className="w-4 h-4 mr-2" />
                            Save as Template
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => setDeleteFormId(form.id)}
                          className="text-red-600 focus:text-red-600"
                          data-testid={`delete-form-${form.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>Created {formatDate(form.created_at)}</span>
                    <span className={`px-2 py-0.5 rounded-full ${form.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {form.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteFormId} onOpenChange={() => setDeleteFormId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Form</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this form? All responses will also be deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteForm}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Link Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Share Form</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-500">
              Share this link with anyone to collect responses:
            </p>
            <div className="flex gap-2">
              <Input
                id="share-link-input"
                value={shareLink}
                readOnly
                className="flex-1"
                onClick={(e) => e.target.select()}
              />
              <Button
                onClick={copyToClipboard}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Templates Gallery Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="templates-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">Template Gallery</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {loadingTemplates ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : templates.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No templates available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((tmpl) => {
                  const categoryColors = {
                    feedback: 'bg-blue-50 text-blue-700 border-blue-200',
                    registration: 'bg-green-50 text-green-700 border-green-200',
                    survey: 'bg-purple-50 text-purple-700 border-purple-200',
                    quiz: 'bg-amber-50 text-amber-700 border-amber-200',
                    contact: 'bg-rose-50 text-rose-700 border-rose-200',
                    custom: 'bg-slate-50 text-slate-700 border-slate-200'
                  };
                  const colorCls = categoryColors[tmpl.category] || categoryColors.custom;
                  return (
                    <Card key={tmpl.id} className="hover:shadow-md transition-shadow cursor-pointer border-slate-200" data-testid={`template-card-${tmpl.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-medium text-slate-900 font-['Outfit']">{tmpl.name}</h3>
                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{tmpl.description}</p>
                          </div>
                          {tmpl.is_default && <Star className="w-4 h-4 text-amber-400 flex-shrink-0" />}
                        </div>
                        <div className="flex items-center justify-between mt-4">
                          <span className={`text-xs px-2 py-1 rounded-full border ${colorCls}`}>
                            {tmpl.category || 'custom'}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{tmpl.fields?.length || 0} fields</span>
                            <Button size="sm" onClick={() => handleUseTemplate(tmpl.id)} className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" data-testid={`use-template-${tmpl.id}`}>
                              Use Template
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplates(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
